/**
 * همه‌ی کارهای ادمین روی یک رخداد/بیزینس — نسخه‌ی اپِ ادمین.
 *
 * قرینه‌ی `apps/mobile/app/admin/item/[id].tsx` و جانشینِ
 * `apps/web/src/features/admin/AdminItemPage.tsx` که با همین تغییر حذف شد.
 *
 * ═══ چه چیزی عمداً فرق دارد ═══
 *
 * ۱. **مسیر نیست، حالت است.** اپِ ادمین روتر ندارد و سربرگ‌هایش با state
 *    عوض می‌شوند. پس «رفتن به آیتم» یعنی `selectedId` در ReviewTab، نه
 *    `/admin/item/:id`. یک ابزارِ تک‌صفحه‌ایِ داخلی به تاریخچه‌ی مرورگر
 *    نیازی ندارد.
 *
 * ۲. **کارت‌ها جدول شدند.** منابع، تکراری‌ها، ادغام‌ها و لاگ همه ردیفِ فشرده‌اند
 *    نه کارتِ بزرگ. مخاطبِ این اپ می‌خواهد ده ردیف را در یک نگاه ببیند.
 *
 * ۳. **فارسیِ درجا، نه `t()`.** هیچ‌جای این اپ i18n ندارد؛ مخاطبش یک نفر است
 *    و اضافه کردنِ واژه‌نامه فقط بسته را بزرگ می‌کند.
 *
 * ⚠️ **ابزارِ ترکیبِ چندتایی این‌جا هم نیامد** و همچنان فقط موبایل دارد
 * (`apps/mobile/src/features/admin/combine.ts`). دلیلش همان دلیلِ نبودنش در
 * سایت است: آن ابزار برای ادغامِ فیلد-به-فیلدِ چند نسخه‌ی نصفه‌ونیمه در یک
 * رکوردِ تازه است — کاری کمیاب. مسیرِ رایج (دو تکراری، یکی می‌ماند) همین‌جا
 * با «نگه‌داشتنِ این / آن» پوشش داده شده. ساختنِ دوباره‌اش قبل از اینکه
 * کسی واقعاً لازمش داشته باشد، یعنی نگه‌داریِ سومین نسخه‌ی یک ابزارِ کمیاب.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminRepo, itemLabel } from '@/core/data/adminRepo';
import { eventRepo } from '@/core/data/miscRepo';
import { formatDateTime } from '@/core/lib/dates';
import { reportError } from '@/core/lib/report';
import type { AdminAction, DuplicateCandidate, ItemSource } from '@/core/domain/types';
import { Modal } from '@/ui/Modal';

const STATUS: Record<string, string> = {
  pending_review: 'در انتظارِ بازبینی', published: 'منتشرشده', rejected: 'ردشده',
  superseded: 'جایگزین‌شده', expired: 'منقضی', merged: 'ادغام‌شده',
};

const SOURCE_KIND: Record<ItemSource['kind'], string> = {
  primary: 'منبعِ اصلی', source_corroboration: 'تاییدِ منبعِ دیگر',
  admin: 'ادمین', ai: 'هوش مصنوعی', attendee_confirmed: 'تاییدِ شرکت‌کننده',
};

const DUP_REASON: Record<DuplicateCandidate['reason'], string> = {
  hash: 'هشِ یکسان', url: 'آدرسِ یکسان', title: 'عنوانِ یکسان',
  when: 'زمانِ یکسان', prefix: 'پیشوندِ یکسان', similar: 'شباهتِ متنی',
};

const ACTION: Record<AdminAction['action'], string> = {
  delete: 'حذف', restore: 'بازگردانی', flag: 'نشان‌دار شد', unflag: 'نشان برداشته شد',
  merge: 'ادغام', unmerge: 'برگشتِ ادغام', dismiss_dup: 'تکراری نیست',
  restore_dup: 'دوباره تکراری', combine: 'ترکیب',
  // مایگریشن 0096: ویرایشِ درجای ادمین روی ردیفِ کرالری، و برداشتنِ قفل.
  edit: 'ویرایش', unlock: 'قفل برداشته شد',
  // تنها کنشی که کارِ ادمین نیست — خودِ فرستنده پسش گرفته (مایگریشن 0078).
  withdraw: 'صاحبش پس گرفت',
};

const domainOf = (url?: string | null): string =>
  (url ? url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') : '');

/** دو لینک وقتی «همان» شمرده می‌شوند که فقط در پروتکل/www/اسلشِ آخر فرق کنند. */
const sameLink = (a?: string | null, b?: string | null): boolean => {
  const norm = (u?: string | null) => (u ?? '').trim().toLowerCase()
    .replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
  return !!norm(a) && norm(a) === norm(b);
};

export function ItemDetail({ id, onBack, onOpen }: {
  id: string;
  onBack: () => void;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [sheet, setSheet] = useState<'delete' | 'flag' | 'merge' | null>(null);
  const [reason, setReason] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeNote, setMergeNote] = useState('');
  const [mergeLoser, setMergeLoser] = useState<{ id: string; shortId?: number | null } | null>(null);
  const [dismissTarget, setDismissTarget] = useState<DuplicateCandidate | null>(null);
  const [dismissNote, setDismissNote] = useState('');

  const item = useQuery({ queryKey: ['adminItem', id], queryFn: () => adminRepo.detail(id) });
  const e = item.data;
  const rootId = e?.rootId ?? e?.id ?? id;
  const on = !!e;

  const sources = useQuery({
    queryKey: ['adminSources', rootId], queryFn: () => adminRepo.sources(rootId), enabled: on,
  });
  const dups = useQuery({
    queryKey: ['adminDuplicates', id], queryFn: () => adminRepo.duplicates(id), enabled: on,
  });
  const dupIds = (dups.data ?? []).map((d) => d.id).sort();
  const dupRows = useQuery({
    queryKey: ['adminItems', dupIds], queryFn: () => adminRepo.items(dupIds), enabled: dupIds.length > 0,
  });
  const rowOf = (dupId: string) => (dupRows.data ?? []).find((r) => r.id === dupId);
  const dismissed = useQuery({
    queryKey: ['adminDismissedDups', id], queryFn: () => adminRepo.dismissedDuplicates(id), enabled: on,
  });
  const merges = useQuery({
    queryKey: ['adminMerges', rootId], queryFn: () => adminRepo.merges(rootId), enabled: on,
  });
  const actions = useQuery({
    queryKey: ['adminActions', rootId], queryFn: () => adminRepo.actions(rootId), enabled: on,
  });
  const versionCount = useQuery({
    queryKey: ['eventVersionCount', rootId], queryFn: () => eventRepo.versionCount(rootId), enabled: on,
  });

  const loserShortId = mergeLoser?.shortId ?? e?.shortId;
  const targetNum = Number(mergeTarget.trim());
  const targetOk = Number.isInteger(targetNum) && targetNum > 0 && targetNum !== loserShortId;
  const target = useQuery({
    queryKey: ['adminByShortId', targetNum],
    queryFn: () => adminRepo.byShortId(targetNum),
    enabled: sheet === 'merge' && targetOk,
  });

  const invalidate = () => {
    for (const key of [
      ['adminItem', id], ['adminSources', rootId], ['adminDuplicates', id],
      ['adminDismissedDups', id], ['adminMerges', rootId], ['adminActions', rootId],
      ['eventVersionCount', rootId], ['eventsPendingReview'], ['adminFlagged'],
      ['adminMergedList'],
    ]) qc.invalidateQueries({ queryKey: key });
  };

  const closeSheets = () => {
    setSheet(null); setReason(''); setFlagNote('');
    setMergeTarget(''); setMergeNote(''); setMergeLoser(null);
  };

  const del = useMutation({
    mutationFn: () => adminRepo.softDelete(e!.id, reason),
    onSuccess: () => { closeSheets(); invalidate(); },
    onError: (err) => reportError('admin.item.delete', err),
  });
  const restore = useMutation({
    mutationFn: () => adminRepo.restore(e!.id),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.item.restore', err),
  });
  const flag = useMutation({
    mutationFn: (v: boolean) => adminRepo.setFlag(e!.id, v, flagNote),
    onSuccess: () => { closeSheets(); invalidate(); },
    onError: (err) => reportError('admin.item.flag', err),
  });
  const merge = useMutation({
    mutationFn: (shortId: number) => adminRepo.merge(mergeLoser?.id ?? e!.id, shortId, mergeNote),
    onSuccess: () => { closeSheets(); invalidate(); },
    onError: (err) => reportError('admin.item.merge', err),
  });
  const dismiss = useMutation({
    mutationFn: () => adminRepo.dismissDuplicate(e!.id, dismissTarget!.id, dismissNote),
    onSuccess: () => { setDismissTarget(null); setDismissNote(''); invalidate(); },
    onError: (err) => reportError('admin.item.dismissDup', err),
  });
  const undismiss = useMutation({
    mutationFn: (otherId: string) => adminRepo.restoreDuplicate(e!.id, otherId),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.item.restoreDup', err),
  });
  const unmerge = useMutation({
    mutationFn: (mergeId: number) => adminRepo.unmerge(mergeId),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.item.unmerge', err),
  });
  // `undefined` یعنی همه‌ی قفل‌ها (مایگریشن 0096).
  const unlock = useMutation({
    mutationFn: (fields: string[] | undefined) => adminRepo.unlockFields(e!.id, fields),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.item.unlock', err),
  });

  if (item.isLoading) return <p className="dim">…</p>;
  if (!e) {
    return (
      <div className="card">
        <p className="dim">این آیتم پیدا نشد.</p>
        <button className="btn" type="button" onClick={onBack}>برگشت به صف</button>
      </div>
    );
  }

  const label = itemLabel(e);
  const locked = e.lockedFields ?? [];
  const isDeleted = e.status === 'rejected';
  const isMerged = e.status === 'merged';
  const openMerge = (merges.data ?? []).find((m) => !m.undoneAt && m.loserId === e.id);

  const proposeMerge = (winnerShortId: number, loser?: { id: string; shortId?: number | null }) => {
    setMergeLoser(loser ?? null);
    setMergeTarget(String(winnerShortId));
    setSheet('merge');
  };

  return (
    <>
      <div className="topbar" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <button className="tab" type="button" onClick={onBack}>← صف</button>
        <span className="mono dim" dir="ltr">#{label}</span>
        <span className="spacer" />
      </div>

      <div className="card">
        <h2 style={{ marginBottom: 6 }}>{e.title}</h2>
        <div className="chiprow">
          <span className={`chip${e.status === 'published' ? ' on' : ''}`}>
            {STATUS[e.status ?? ''] ?? e.status}
          </span>
          <span className="chip">{e.kind === 'business' ? 'مکان' : 'رخداد'}</span>
          {e.source ? <span className="chip mono">{e.source}</span> : null}
          {e.flaggedAt ? <span className="chip warn">نشان‌دار</span> : null}
          <span className="chip">{versionCount.data ?? 1} نسخه</span>
          {e.url ? (
            <a className="chip" href={e.url} target="_blank" rel="noreferrer" dir="ltr">
              {domainOf(e.url)}
            </a>
          ) : null}
          {/* لینکِ بلیط جدا از لینکِ معرفی (مایگریشن 0077) — موقعِ بازبینی
              باید هر دو چک شن، پس هر دو دیده می‌شن. */}
          {e.ticketUrl ? (
            <a className="chip" href={e.ticketUrl} target="_blank" rel="noreferrer" dir="ltr">
              بلیط: {domainOf(e.ticketUrl)}
            </a>
          ) : null}
          {/* ⚠ ادعای فرستنده، نه چیزی که ما تایید کرده باشیم. مهمه چون همین
              ادعا تعیین می‌کنه ویرایش‌های بعدیِ خودش بازبینی می‌خواد یا نه
              (نگاه کن به `save_own_event`). */}
          {e.isOrganizer ? <span className="chip warn">می‌گه برگزارکننده‌ست</span> : null}
        </div>

        {e.flaggedAt ? (
          <p className="note">
            {e.flagNote || 'نشان‌دار شده'}
            {e.flaggedByName ? ` — ${e.flaggedByName}` : ''}
          </p>
        ) : null}
        {isMerged ? (
          <p className="note">
            ادغام شده در #{e.mergedIntoShortId ?? '?'}
            {e.mergedAt ? ` · ${formatDateTime(e.mergedAt)}` : ''}
          </p>
        ) : null}

        <div className="chiprow" style={{ marginTop: 10 }}>
          {isDeleted ? (
            <button className="btn" type="button" disabled={restore.isPending}
              onClick={() => restore.mutate()}>بازگردانی</button>
          ) : (
            <button className="btn ghost" type="button" onClick={() => setSheet('delete')}>حذف</button>
          )}
          {e.flaggedAt ? (
            <button className="btn ghost" type="button" disabled={flag.isPending}
              onClick={() => flag.mutate(false)}>برداشتنِ نشان</button>
          ) : (
            <button className="btn ghost" type="button" onClick={() => setSheet('flag')}>نشان‌دار کن</button>
          )}
          {isMerged && openMerge ? (
            <button className="btn" type="button" disabled={unmerge.isPending}
              onClick={() => unmerge.mutate(openMerge.id)}>برگرداندنِ ادغام</button>
          ) : (
            <button className="btn ghost" type="button" onClick={() => setSheet('merge')}>ادغام</button>
          )}
        </div>
      </div>

      {/* قفلِ فیلد (مایگریشن 0096). کارتی که فقط وقتی چیزی قفل باشد دیده
          می‌شود: روی یک ردیفِ دست‌نخورده‌ی کرالری فقط سر و صدا بود.
          نامِ ستون خام نوشته می‌شود («starts_at» و نه «ساعتِ شروع») چون
          قراردادش با کرالر است و مخاطبِ این صفحه همان اسم را می‌شناسد. */}
      {locked.length > 0 ? (
        <div className="card">
          <h2>فیلدهای قفل‌شده</h2>
          <p className="dim">
            کرالر به این‌ها دست نمی‌زند. قفل که برداشته شود، اجرای بعدی‌اش
            دوباره رویشان می‌نویسد — مقدارِ قبلیِ کرالر برنمی‌گردد، چون
            تاریخچه‌اش روی دیتابیسِ کرالر است نه این‌جا.
          </p>
          <div className="chiprow" style={{ marginTop: 8 }}>
            {locked.map((f) => (
              <button key={f} className="chip mono" type="button" disabled={unlock.isPending}
                title="بگذار کرالر دوباره مدیریتش کند"
                onClick={() => unlock.mutate([f])}>
                🔒 {f} ✕
              </button>
            ))}
          </div>
          <div className="chiprow" style={{ marginTop: 10 }}>
            <button className="btn ghost" type="button" disabled={unlock.isPending}
              onClick={() => unlock.mutate(undefined)}>
              همه را آزاد کن
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        <h2>منابع</h2>
        {(sources.data ?? []).length === 0 ? <p className="dim">منبعی ثبت نشده.</p> : (
          <div className="scrollx">
            <table className="tbl">
              <thead><tr><th>نام</th><th>نوع</th><th>شناسه</th><th>کِی</th></tr></thead>
              <tbody>
                {(sources.data ?? []).map((s, i) => (
                  <tr key={`${s.kind}-${s.name}-${i}`}>
                    <td>{s.name}</td>
                    <td className="dim">{SOURCE_KIND[s.kind] ?? s.kind}</td>
                    <td className="mono dim">{s.externalId ?? s.note ?? ''}</td>
                    <td className="dim">{s.at ? formatDateTime(s.at) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>تکراری‌های محتمل</h2>
        {(dups.data ?? []).length === 0 ? <p className="dim">تکراری‌ای پیدا نشد.</p> : null}
        {(dups.data ?? []).map((d) => {
          const other = rowOf(d.id);
          const myUrl = e.url?.trim() || '';
          const dupUrl = (other?.url ?? d.url)?.trim() || '';
          return (
            <div key={d.id} className="dup">
              <div className="dup-head">
                <b>{d.title}</b>
                <span className="mono dim">#{d.shortId}</span>
                <span className="chip">{DUP_REASON[d.reason] ?? d.reason}</span>
                {d.startsAt ? <span className="dim">{formatDateTime(d.startsAt)}</span> : null}
              </div>
              {/* لینکِ هر دو طرف کنارِ هم: در عمل همین یک نگاه بیشترِ
                  تصمیم‌ها را می‌گیرد، چون دو صفحه‌ی یکسان یعنی یک رخداد. */}
              {sameLink(myUrl, dupUrl) ? (
                <a className="dup-link" href={myUrl} target="_blank" rel="noreferrer" dir="ltr">
                  همان لینک — {myUrl}
                </a>
              ) : (
                <>
                  {myUrl ? <a className="dup-link" href={myUrl} target="_blank" rel="noreferrer" dir="ltr">#{e.shortId ?? '?'} — {myUrl}</a> : null}
                  {dupUrl ? <a className="dup-link" href={dupUrl} target="_blank" rel="noreferrer" dir="ltr">#{d.shortId} — {dupUrl}</a> : null}
                </>
              )}
              <div className="chiprow" style={{ marginTop: 6 }}>
                <button className="btn ghost" type="button" onClick={() => onOpen(d.id)}>باز کن</button>
                {!isMerged ? (
                  <>
                    <button className="btn" type="button"
                      onClick={() => proposeMerge(e.shortId ?? 0, { id: d.id, shortId: d.shortId })}>
                      این را نگه دار (#{e.shortId ?? '?'})
                    </button>
                    <button className="btn" type="button" onClick={() => proposeMerge(d.shortId)}>
                      آن را نگه دار (#{d.shortId})
                    </button>
                  </>
                ) : null}
                <button className="btn ghost" type="button"
                  onClick={() => setDismissTarget(d)}>تکراری نیست</button>
              </div>
            </div>
          );
        })}

        {(dismissed.data ?? []).length > 0 ? (
          <>
            <h3 style={{ marginTop: 12 }}>«تکراری نیست»های ثبت‌شده</h3>
            <div className="scrollx">
              <table className="tbl">
                <tbody>
                  {(dismissed.data ?? []).map((n) => (
                    <tr key={n.id}>
                      <td>{n.title}</td>
                      <td className="mono dim">#{n.shortId}</td>
                      <td className="dim">
                        {formatDateTime(n.createdAt)}{n.byName ? ` · ${n.byName}` : ''}
                        {n.note ? ` · ${n.note}` : ''}
                      </td>
                      <td>
                        <button className="btn ghost" type="button"
                          onClick={() => onOpen(n.id)}>باز کن</button>
                        <button className="btn ghost" type="button" disabled={undismiss.isPending}
                          onClick={() => undismiss.mutate(n.id)}>دوباره تکراری</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      {(merges.data ?? []).length > 0 ? (
        <div className="card">
          <h2>ادغام‌ها</h2>
          <div className="scrollx">
            <table className="tbl">
              <thead><tr><th>از → به</th><th>عنوان</th><th>کِی</th><th /></tr></thead>
              <tbody>
                {(merges.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <td className="mono">#{m.loserShortId ?? '?'} → #{m.winnerShortId ?? '?'}</td>
                    <td className="dim">{m.loserTitle} → {m.winnerTitle}{m.note ? ` · ${m.note}` : ''}</td>
                    <td className="dim">
                      {formatDateTime(m.mergedAt)}{m.mergedByName ? ` · ${m.mergedByName}` : ''}
                      {m.undoneAt ? ' · برگردانده شد' : ''}
                    </td>
                    <td>
                      {!m.undoneAt ? (
                        <button className="btn ghost" type="button" disabled={unmerge.isPending}
                          onClick={() => unmerge.mutate(m.id)}>برگرداندن</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="card">
        <h2>تاریخچه</h2>
        {(actions.data ?? []).length === 0 ? <p className="dim">هنوز کاری روی این آیتم ثبت نشده.</p> : (
          <div className="scrollx">
            <table className="tbl">
              <tbody>
                {(actions.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td>{ACTION[a.action] ?? a.action}</td>
                    <td className="dim">{a.actorName ?? ''}</td>
                    <td className="dim">
                      {typeof a.detail?.reason === 'string' ? a.detail.reason as string : ''}
                      {typeof a.detail?.note === 'string' ? a.detail.note as string : ''}
                      {/* برای `edit`/`unlock` تنها جایی است که «کدام ستون»
                          نوشته می‌شود — زنجیره‌ی نسخه‌ها برای ردیفِ کرالری
                          دیگر ساخته نمی‌شود (مایگریشن 0096). */}
                      {Array.isArray(a.detail?.fields) ? (
                        <span className="mono" dir="ltr">
                          {' '}{(a.detail.fields as string[]).join(', ')}
                        </span>
                      ) : null}
                    </td>
                    <td className="dim">{formatDateTime(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* داده‌ی خام آخر می‌آید و باز است: وقتی چیزی با هیچ‌کدام از بخش‌های
          بالا جور در نمی‌آید، این تنها جایی است که جواب دارد. */}
      <div className="card">
        <h2>داده‌ی خام</h2>
        <p className="dim">metadata</p>
        <pre className="raw-json">{JSON.stringify(e.metadata, null, 2)}</pre>
        <p className="dim">ردیفِ جدول</p>
        <pre className="raw-json">{JSON.stringify(e.raw, null, 2)}</pre>
      </div>

      {sheet === 'delete' ? (
        <Modal title="حذف" hint="آیتم نرم‌حذف می‌شود و قابلِ بازگردانی است." onClose={closeSheets}>
          <textarea className="input textarea" value={reason}
            onChange={(ev) => setReason(ev.target.value)} placeholder="دلیل" />
          <button className="btn block" type="button" disabled={del.isPending}
            onClick={() => del.mutate()}>{del.isPending ? '…' : 'حذف'}</button>
        </Modal>
      ) : null}

      {sheet === 'flag' ? (
        <Modal title="نشان‌دار کردن" hint="برای چیزی که باید بعداً دوباره نگاهش کرد." onClose={closeSheets}>
          <textarea className="input textarea" value={flagNote}
            onChange={(ev) => setFlagNote(ev.target.value)} placeholder="یادداشت" />
          <button className="btn block" type="button" disabled={flag.isPending}
            onClick={() => flag.mutate(true)}>{flag.isPending ? '…' : 'نشان‌دار کن'}</button>
        </Modal>
      ) : null}

      {dismissTarget ? (
        <Modal title="تکراری نیست"
          hint="این جفت دیگر به‌عنوانِ تکراری پیشنهاد نمی‌شود."
          onClose={() => setDismissTarget(null)}>
          <p className="note">
            #{e.shortId ?? '?'} ↮ #{dismissTarget.shortId} — {dismissTarget.title}
          </p>
          <textarea className="input textarea" value={dismissNote}
            onChange={(ev) => setDismissNote(ev.target.value)} placeholder="یادداشت" />
          <button className="btn block" type="button" disabled={dismiss.isPending}
            onClick={() => dismiss.mutate()}>{dismiss.isPending ? '…' : 'تکراری نیست'}</button>
        </Modal>
      ) : null}

      {sheet === 'merge' ? (
        <Modal title="ادغام" hint="شناسه‌ی عددیِ آیتمی که باید بماند." onClose={closeSheets}>
          <input className="input" value={mergeTarget} dir="ltr"
            onChange={(ev) => setMergeTarget(ev.target.value)} placeholder="مثلاً 1234" />

          {/* وضعیتِ مقصد قبل از زدنِ دکمه گفته می‌شود، نه بعدش: ادغام برگشت
              دارد ولی برگرداندنش کارِ اضافه است. */}
          {mergeTarget.trim().length === 0 ? null
            : !targetOk ? (
              <p className="dim">
                {targetNum === loserShortId ? 'نمی‌شود در خودش ادغام شود.' : 'شناسه‌ی معتبر نیست.'}
              </p>
            ) : target.isFetching ? <p className="dim">…</p>
              : !target.data ? <p className="dim">#{targetNum} پیدا نشد.</p>
                : (
                  <p className="note">
                    #{loserShortId ?? '?'} → #{targetNum} — {target.data.title}
                  </p>
                )}

          <textarea className="input textarea" value={mergeNote}
            onChange={(ev) => setMergeNote(ev.target.value)} placeholder="یادداشت" />
          <button className="btn block" type="button" disabled={!target.data || merge.isPending}
            onClick={() => merge.mutate(targetNum)}>
            {merge.isPending ? '…' : target.data ? `ادغام در #${targetNum}` : 'ادغام'}
          </button>
        </Modal>
      ) : null}
    </>
  );
}
